/**
 * Their page's live wires: one WebSocket into each room's conversation, as the living page has always
 * had. "refresh" re-reads the page, "typing" from the host lights the dots, and a (re)connect reads
 * once so nothing is missed while a wire was down. Only after three failed connects in a row does a
 * slow read stand in, stopped the moment a wire opens.
 */
import { useCallback, useEffect, useRef } from "react";

const RETRY_MS = 3_000;
const SLOW_MS = 30_000;
const TYPING_GAP_MS = 2_500;

export function useRoomWires(
  token: string,
  webinarIds: readonly string[],
  onRefresh: () => void,
  onTyping: (webinarId: string) => void,
): (webinarId: string) => void {
  const wires = useRef(new Map<string, WebSocket>());
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const typing = useRef(onTyping);
  typing.current = onTyping;
  const key = webinarIds.join(",");

  useEffect(() => {
    if (!token || !key || typeof WebSocket === "undefined") return;
    let alive = true;
    const timers: number[] = [];
    let slow: number | undefined;
    let fails = 0;
    const connect = (webinarId: string) => {
      if (!alive) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(
        `${proto}://${location.host}/api/watch/${encodeURIComponent(token)}/ws?webinar_id=${encodeURIComponent(webinarId)}`,
      );
      wires.current.set(webinarId, ws);
      ws.onopen = () => {
        fails = 0;
        window.clearInterval(slow);
        slow = undefined;
        refresh.current();
      };
      ws.onmessage = (e) => {
        let ev: { type?: string; from?: string };
        try {
          ev = JSON.parse(String(e.data));
        } catch {
          return;
        }
        if (ev.type === "refresh") refresh.current();
        if (ev.type === "typing" && ev.from === "host") typing.current(webinarId);
      };
      ws.onclose = () => {
        if (wires.current.get(webinarId) === ws) wires.current.delete(webinarId);
        if (!alive) return;
        fails += 1;
        if (fails >= 3 && slow === undefined) {
          slow = window.setInterval(() => {
            if (document.visibilityState === "visible") refresh.current();
          }, SLOW_MS);
        }
        timers.push(window.setTimeout(() => connect(webinarId), RETRY_MS));
      };
    };
    for (const id of key.split(",")) connect(id);
    const open = wires.current;
    return () => {
      alive = false;
      for (const t of timers) window.clearTimeout(t);
      window.clearInterval(slow);
      for (const ws of open.values()) ws.close();
      open.clear();
    };
  }, [token, key]);

  const last = useRef(0);
  return useCallback((webinarId: string) => {
    const ws = wires.current.get(webinarId);
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - last.current < TYPING_GAP_MS) return;
    last.current = now;
    try {
      ws.send(JSON.stringify({ type: "typing" }));
    } catch {
      /* the wire reconnects on its own */
    }
  }, []);
}

/** Who a place's socket says is typing: the host, or the guest's registration id. */
export interface Typist {
  from?: string;
  rid?: string;
}

/**
 * The wire into one open place (a room's Everyone, a circle): the same pattern as the host's wires above,
 * for the address the place names. "refresh" re-reads it, "typing" says who is typing (by registration id), and the returned
 * function tells the others this person is typing.
 */
export function usePlaceWire(
  path: string | null,
  onRefresh: () => void,
  onTyping: (who: Typist) => void,
): () => void {
  const wire = useRef<WebSocket | null>(null);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const typing = useRef(onTyping);
  typing.current = onTyping;

  useEffect(() => {
    if (!path || typeof WebSocket === "undefined") return;
    let alive = true;
    let timer: number | undefined;
    let slow: number | undefined;
    let fails = 0;
    const connect = () => {
      if (!alive) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${location.host}${path}`);
      wire.current = ws;
      ws.onopen = () => {
        fails = 0;
        window.clearInterval(slow);
        slow = undefined;
        refresh.current();
      };
      ws.onmessage = (e) => {
        let ev: { type?: string; from?: string; rid?: string };
        try {
          ev = JSON.parse(String(e.data));
        } catch {
          return;
        }
        if (ev.type === "refresh") refresh.current();
        if (ev.type === "typing") typing.current({ from: ev.from, rid: ev.rid });
      };
      ws.onclose = () => {
        if (wire.current === ws) wire.current = null;
        if (!alive) return;
        fails += 1;
        if (fails >= 3 && slow === undefined) {
          slow = window.setInterval(() => {
            if (document.visibilityState === "visible") refresh.current();
          }, SLOW_MS);
        }
        timer = window.setTimeout(connect, RETRY_MS);
      };
    };
    connect();
    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.clearInterval(slow);
      wire.current?.close();
      wire.current = null;
    };
  }, [path]);

  const last = useRef(0);
  return useCallback(() => {
    const ws = wire.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - last.current < TYPING_GAP_MS) return;
    last.current = now;
    try {
      ws.send(JSON.stringify({ type: "typing" }));
    } catch {
      /* the wire reconnects on its own */
    }
  }, []);
}

/**
 * The wires of every room and circle the person has, open whatever place they are on, so a post or a
 * line anywhere re-reads What's new and the badges. A place's own wire only lives while it is open.
 */
export function useNewsWires(paths: readonly string[], onRefresh: () => void): void {
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  const key = paths.join(",");

  useEffect(() => {
    if (!key || typeof WebSocket === "undefined") return;
    let alive = true;
    const open = new Set<WebSocket>();
    const timers: number[] = [];
    const connect = (path: string) => {
      if (!alive) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${location.host}${path}`);
      open.add(ws);
      ws.onmessage = (e) => {
        try {
          if ((JSON.parse(String(e.data)) as { type?: string }).type === "refresh") refresh.current();
        } catch {
          /* not a poke */
        }
      };
      ws.onclose = () => {
        open.delete(ws);
        if (alive) timers.push(window.setTimeout(() => connect(path), RETRY_MS));
      };
    };
    for (const path of key.split(",")) connect(path);
    return () => {
      alive = false;
      for (const t of timers) window.clearTimeout(t);
      for (const ws of open) ws.close();
    };
  }, [key]);
}
